"use client";

import { motion, useMotionValueEvent, useScroll, useTransform } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";

interface TimelineEntry {
  title: string;
  content: React.ReactNode;
}

export const Timeline = ({ data }: { data: TimelineEntry[] }) => {
  const ref = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start 80%", "end 50%"],
  });

  useEffect(() => {
    if (!ref.current) return;
    setHeight(ref.current.getBoundingClientRect().height);
  }, []);

  // Closer to the original: keep height synced while scrolling
  useMotionValueEvent(scrollYProgress, "change", () => {
    if (!ref.current) return;
    setHeight(ref.current.getBoundingClientRect().height);
  });

  const heightTransform = useTransform(scrollYProgress, [0, 1], [0, height]);
  const opacityTransform = useTransform(scrollYProgress, [0, 0.1], [0, 1]);

  return (
    <div className="w-full bg-background font-sans px-4 sm:px-6 md:px-10" ref={containerRef} dir="rtl">
      <div ref={ref} className="relative max-w-7xl mx-auto pb-12 sm:pb-16 md:pb-20 pt-6 sm:pt-8 md:pt-10">
        {data.map((item, index) => (
          <div key={index} className={`flex justify-start gap-2 sm:gap-4 md:gap-10 ${index === 0 ? 'pt-2 sm:pt-4 md:pt-8' : 'pt-8 sm:pt-12 md:pt-32'}`}>
            <div className="sticky flex flex-col md:flex-row z-40 items-center top-24 sm:top-32 md:top-40 self-start max-w-xs lg:max-w-sm md:w-full">
              <div className="h-8 sm:h-10 absolute right-1 sm:right-2 md:right-3 w-8 sm:w-10 rounded-full bg-background flex items-center justify-center">
                <div className="h-3 sm:h-4 w-3 sm:w-4 rounded-full bg-primary/20 border border-primary" />
              </div>
              <h3 className="hidden md:block text-xl md:pr-20 md:text-5xl font-bold text-primary">
                {item.title}
              </h3>
            </div>

            <div className="relative pr-10 sm:pr-12 md:pr-4 pl-2 sm:pl-4 w-full">
              <h3 className="md:hidden block text-xl sm:text-2xl mb-3 sm:mb-4 text-right font-bold text-primary">
                {item.title}
              </h3>
              {item.content}
            </div>
          </div>
        ))}

        <div
          style={{ height: height + "px" }}
          className="absolute right-5 sm:right-6 md:right-8 top-0 overflow-hidden w-[2px] bg-[linear-gradient(to_bottom,var(--tw-gradient-stops))] from-transparent from-[0%] via-border to-transparent to-[99%] [mask-image:linear-gradient(to_bottom,transparent_0%,black_10%,black_90%,transparent_100%)]"
        >
          <motion.div
            style={{ height: heightTransform, opacity: opacityTransform }}
            className="absolute inset-x-0 top-0 w-[2px] bg-gradient-to-t from-primary via-gold to-transparent from-[0%] via-[10%] rounded-full shadow-[0_0_8px_2px_hsl(var(--gold)/0.6),0_0_16px_4px_hsl(var(--primary)/0.4)]"
          />
        </div>
      </div>
    </div>
  );
};

